import React, { useState, useEffect } from "react";
import { LoaderCircle } from "lucide-react";
import { toast } from "sonner";
import { useAuth as useCognitoAuth } from "react-oidc-context";
import { useAuth } from "../context/AuthContext";

// Safely get environment variable without throwing an error
const getEnvVar = (name: string): string | null => {
  return process.env[name] || null;
};

// Check if we're in Cognito environment (web)
const isCognitoEnvironment = (): boolean => {
  // If all these required Cognito env vars are present, we're in Cognito environment
  const cognitoClientId = getEnvVar("REACT_APP_COGNITO_CLIENT_ID");
  const redirectUri = getEnvVar("REACT_APP_REDIRECT_URI");
  const cognitoDomain = getEnvVar("REACT_APP_COGNITO_DOMAIN");
  
  return !!cognitoClientId && !!redirectUri && !!cognitoDomain;
};

// Sign out redirect with safe env var access
const signOutRedirect = () => {
  const clientId = getEnvVar("REACT_APP_COGNITO_CLIENT_ID");
  const logoutUri = getEnvVar("REACT_APP_REDIRECT_URI");
  const cognitoDomain = getEnvVar("REACT_APP_COGNITO_DOMAIN");
  
  // Check if we have all required env vars
  if (!clientId || !logoutUri || !cognitoDomain) {
    console.warn("Missing environment variables for Cognito logout redirect");
    return false;
  }
  
  window.location.href = `${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(
    logoutUri
  )}`;
  return true;
};

const Unauthorized: React.FC = () => {
  const auth = useCognitoAuth();
  const { authMode } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isCognitoEnv, setIsCognitoEnv] = useState(false);

  // Determine environment on component mount
  useEffect(() => {
    const cognitoEnv = isCognitoEnvironment();
    setIsCognitoEnv(cognitoEnv);
    console.log(`Environment detected: ${cognitoEnv ? 'Cognito Web' : 'Teams'}`);
  }, []);

  // Handle Cognito login redirect
  const handleLoginRedirect = async () => {
    setIsLoading(true);
    
    try {
      // First, try to remove the user (this clears local tokens)
      await auth.removeUser();
      
      // Then redirect to Cognito's login page
      const redirectSuccess = signOutRedirect();
      
      if (!redirectSuccess) {
        toast.error("Failed to redirect to login page. Missing configuration.");
        setIsLoading(false);
      } else {
        toast.success("Redirecting to login page...");
      }
    } catch (error) {
      console.error("Redirect error:", error);
      
      // If removeUser fails, try the direct redirect as a fallback
      const redirectSuccess = signOutRedirect();
      
      if (!redirectSuccess) {
        toast.error("Failed to redirect to login page");
        setIsLoading(false);
      }
    }
  };

  // Determine if we should show the login button
  // Only show in Cognito environment
  const shouldShowLoginButton = isCognitoEnv && authMode === 'cognito';

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-violet-50 text-center">
      <div className="p-8 bg-white shadow-lg rounded-lg max-w-md">
        <h1 className="text-2xl font-bold text-violet-700">Session Expired</h1>
        <p className="mt-4 text-md text-gray-600">
          Your session has expired or you are not authorized to view this page.
        </p>
        
        {/* Only show the button in Cognito web environment */}
        {shouldShowLoginButton && (
          <div className="mt-6">
            <button 
              onClick={handleLoginRedirect}
              disabled={isLoading}
              className="bg-violet-600 hover:bg-violet-700 text-white py-2 px-4 rounded-md transition-colors flex items-center justify-center min-w-40"
            >
              {isLoading ? (
                <>
                  <LoaderCircle size={16} className="animate-spin mr-2" />
                  Redirecting...
                </>
              ) : (
                "Log In Again"
              )}
            </button>
          </div>
        )}
        
        {/* For Teams environment, show message */}
        {!isCognitoEnv && (
          <p className="mt-6 text-sm text-gray-500">
            Please try reopening the application.
          </p>
        )}
      </div>
    </div>
  );
};

export default Unauthorized;
